import { useState, useEffect } from 'react';
import * as identity from './identityContract';

/**
 * Cluster 2 (RBAC1 tenant-admin gate) — UI convenience hook.
 *
 * Returns whether the current user is a tenant admin, read from the identity
 * contract's whoAmI (the Library bootstrap seam — the same source App.js's nav
 * gate uses). Role is NOT taken from the Supabase session; it comes through the
 * identity seam so capture-UI stays auth-engine-neutral.
 *
 * This is a HIDE-FOR-CLARITY signal only (Decision 7 / A9). Authoritative
 * enforcement is server-side: accessPolicy `adminOnly` in capture-service and
 * `requireRole('admin')` in the library backend. Never rely on this for
 * security — a non-admin who reaches a gated endpoint still gets 403.
 *
 * `isAdmin` starts false and flips true only once whoAmI resolves for an admin,
 * so admin-only controls stay hidden during the brief load rather than
 * flashing in for everyone.
 */
export function useIsAdmin() {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let active = true;
    identity
      .whoAmI()
      .then((r) => {
        if (active && r.ok) setIsAdmin(r.data.role === 'admin');
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  return isAdmin;
}

/**
 * Workflow-authoring capability (2026-06-14 power_user unit). True for role ∈
 * {admin, power_user} — the builders. Plain members are not. Mirrors useIsAdmin
 * (role from the whoAmI identity seam; starts false so authoring affordances
 * stay hidden during load). HIDE-FOR-CLARITY only — the server enforces via
 * canBuildWorkflows (accessPolicy buildOnly + the PUT draft gate).
 *
 * Returns `undefined` until whoAmI resolves, so callers that must distinguish
 * "still loading" from "not a builder" (e.g. a route guard, to avoid bouncing a
 * builder mid-load) can wait. `false`/`undefined` are both non-builder for a
 * plain boolean check.
 */
export function useCanBuildWorkflows() {
  const [canBuild, setCanBuild] = useState(undefined);
  useEffect(() => {
    let active = true;
    identity
      .whoAmI()
      .then((r) => {
        if (active && r.ok) setCanBuild(['admin', 'power_user'].includes(r.data.role));
        else if (active) setCanBuild(false);
      })
      .catch(() => {
        if (active) setCanBuild(false);
      });
    return () => {
      active = false;
    };
  }, []);
  return canBuild;
}
